import React, { ReactElement, useEffect, useState } from 'react';
import { OrderDto, OrderStatus, ReceiptDto, ReceiptType, WaybillDto } from '../../api/ClientApi';
import { WarehouseClient } from '../../api/Extensions';
import { useAppSelector, useAppDispatch } from '../../hooks/hooks';
import { RootState } from '../../redux/reducers';
import { Box, Typography, DialogContent, Grid2 } from '@mui/material';
import { ReceiptTypeSR, OrderTypeSR } from '../../common/Types';
import { CompareTwoDateTimes, FormatDateText, FormatTimeText } from '../../common/datesHelper';
import { getSiteOrdersTableData, getClientNameInfo } from './OrdersHelper';

import { FormDialogTitle } from '../../common/components/form/FormPrimitives';

import { ResponsiveDialog } from '../../common/components/mobile/MobilePrimitives';

type ReceiptsListProps = {
    closeForm(): void;
    open: boolean;
    orderId?: number;
};

export default function ReceiptsList(props: ReceiptsListProps): ReactElement {
    const { open, closeForm, orderId } = props;

    const sites = useAppSelector((state: RootState) => state.sites.sites);
    const clients = useAppSelector((state: RootState) => state.clients.clients);

    const [order, setOrder] = useState<OrderDto | undefined>(undefined);

    const [originInfo, setOriginInfo] = useState<string>('/');
    const [destinationInfo, setDestinationInfo] = useState<string>('/');

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (!!orderId && open) {
            WarehouseClient.getOrder(orderId, true, true, true).then((res) => {
                const currDeliveryReceipts = res.receipts
                    ?.filter((rec) => rec.receiptType === ReceiptType.Delivery)
                    .sort((a, b) =>
                        CompareTwoDateTimes(a.created ?? new Date(), b.created ?? new Date(), false) ? -1 : 1,
                    );
                const currReceptionReceipts = res.receipts
                    ?.filter((rec) => rec.receiptType === ReceiptType.Reception)
                    .sort((a, b) =>
                        CompareTwoDateTimes(a.created ?? new Date(), b.created ?? new Date(), false) ? -1 : 1,
                    );
                const currReturnReceipts = res.receipts
                    ?.filter((rec) => rec.receiptType === ReceiptType.Return)
                    .sort((a, b) =>
                        CompareTwoDateTimes(a.created ?? new Date(), b.created ?? new Date(), false) ? -1 : 1,
                    );
                const currTransferReceipts = res.receipts
                    ?.filter((rec) => rec.receiptType === ReceiptType.Transfer)
                    .sort((a, b) =>
                        CompareTwoDateTimes(a.created ?? new Date(), b.created ?? new Date(), false) ? -1 : 1,
                    );

                const currReceipts = [];
                if (!!currDeliveryReceipts && currDeliveryReceipts.length > 0) {
                    currReceipts.push(currDeliveryReceipts[currDeliveryReceipts.length - 1]);
                }
                if (!!currReceptionReceipts && currReceptionReceipts.length > 0) {
                    currReceipts.push(currReceptionReceipts[currReceptionReceipts.length - 1]);
                }
                if (!!currReturnReceipts && currReturnReceipts.length > 0) {
                    currReceipts.push(currReturnReceipts[currReturnReceipts.length - 1]);
                }
                if (!!currTransferReceipts && currTransferReceipts.length > 0) {
                    currReceipts.push(currTransferReceipts[currTransferReceipts.length - 1]);
                }

                res.receipts = currReceipts;

                setOrder(res);
            });
        }
    }, [dispatch, open, orderId]);

    useEffect(() => {
        if (!!order) {
            const destinationSite = order.destinationSite;
            const originSite = order.originSite;
            const supplier = order.supplier;

            const destinationData = !destinationSite
                ? '/'
                : getSiteOrdersTableData(destinationSite, sites ?? undefined, clients ?? undefined);

            const originData = !originSite
                ? '/'
                : getSiteOrdersTableData(originSite, sites ?? undefined, clients ?? undefined);

            setOriginInfo(originData);
            setDestinationInfo(destinationData);
        }
    }, [order, open]);

    const ReceiptDetails = (receipt?: ReceiptDto) => {
        if (!receipt) {
            return <></>;
        }

        return (
            <Box>
                <Grid2 container>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body1" component="h6" style={{ fontSize: 18 }}>
                            Vrsta dokumenta:{' '}
                            <span style={{ fontWeight: 'bold' }}>
                                {receipt?.receiptType ? ReceiptTypeSR[receipt?.receiptType] : '/'}
                            </span>
                        </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 0, sm: 6 }} />
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body1" component="h6" style={{ fontSize: 18 }}>
                            Datum izvrsenja:{' '}
                            <span style={{ fontWeight: 'bold' }}>
                                {receipt.executionDate
                                    ? FormatDateText(receipt.executionDate) +
                                      ' ' +
                                      FormatTimeText(receipt.executionDate)
                                    : '/'}
                            </span>
                        </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                        <Typography variant="body1" component="h6" style={{ fontSize: 18 }}>
                            Napomena:
                        </Typography>
                    </Grid2>

                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body1" component="h6" style={{ fontSize: 18 }}>
                            {receipt.description ?? '/'}
                        </Typography>
                    </Grid2>
                    <Grid2
                        size={{ xs: 0, sm: 12 }}
                        style={{ height: 15, margin: 0, padding: 0 }}
                        sx={{ borderBottom: '1px solid rgba(155,155,155,0.5)' }}
                    />
                </Grid2>
            </Box>
        );
    };

    const WaybillDetails = (waybill?: WaybillDto) => {
        if (!waybill) {
            return <></>;
        }

        return (
            <Box>
                <Grid2 container>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body1" component="h6" style={{ fontSize: 18 }}>
                            Vrsta dokumenta: <span style={{ fontWeight: 'bold' }}>Otpremnica</span>
                        </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 0, sm: 6 }} />
                    <Grid2 size={{ xs: 12 }}>
                        <Typography variant="body1" component="h6" style={{ fontSize: 18 }}>
                            Br. dokumenta: <span style={{ fontWeight: 'bold' }}>{waybill.waybillNumber ?? '/'}</span>
                        </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body1" component="h6" style={{ fontSize: 18 }}>
                            Vreme preuzimanja:{' '}
                            <span style={{ fontWeight: 'bold' }}>
                                {waybill.pickupDate
                                    ? FormatDateText(waybill.pickupDate) + ' ' + FormatTimeText(waybill.pickupDate)
                                    : '/'}
                            </span>
                        </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body1" component="h6" style={{ fontSize: 18 }}>
                            Vreme dostave:{' '}
                            <span style={{ fontWeight: 'bold' }}>
                                {waybill.deliveryDate
                                    ? FormatDateText(waybill.deliveryDate) + ' ' + FormatTimeText(waybill.deliveryDate)
                                    : '/'}
                            </span>
                        </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body1" component="h6" style={{ fontSize: 18 }}>
                            Vozač: <span style={{ fontWeight: 'bold' }}>{waybill.driverName ?? '/'}</span>
                        </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body1" component="h6" style={{ fontSize: 18 }}>
                            JMBG vozača: <span style={{ fontWeight: 'bold' }}>{waybill.driverUniqueNumber ?? '/'}</span>
                        </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body1" component="h6" style={{ fontSize: 18 }}>
                            Tablice: <span style={{ fontWeight: 'bold' }}>{waybill.vehicleLicensePlateNum ?? '/'}</span>
                        </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                        <Typography variant="body1" component="h6" style={{ fontSize: 18 }}>
                            Napomena:
                        </Typography>
                    </Grid2>

                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body1" component="h6" style={{ fontSize: 18 }}>
                            {waybill.description ?? '/'}
                        </Typography>
                    </Grid2>
                    <Grid2
                        size={{ xs: 0, sm: 12 }}
                        style={{ height: 15, margin: 0, padding: 0 }}
                        sx={{ borderBottom: '1px solid rgba(155,155,155,0.5)' }}
                    />
                </Grid2>
            </Box>
        );
    };

    return (
        <ResponsiveDialog scroll="body" maxWidth="md" open={open} onClose={closeForm}>
            <FormDialogTitle>Dokumenti za nalog</FormDialogTitle>
            <DialogContent dividers sx={{ px: { xs: 2, sm: 5 }, pt: { xs: 3, sm: 5 } }}>
                <Grid2 container spacing={2} style={{ maxWidth: '800px', minWidth: '600px', margin: 'auto' }}>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body1" component="h6" style={{ fontSize: 18 }}>
                            Vrsta naloga:{' '}
                            <span style={{ fontWeight: 'bold' }}>
                                {order?.orderStatus === OrderStatus.Idle ||
                                order?.orderStatus === OrderStatus.Pending ||
                                order?.orderStatus === OrderStatus.Approved
                                    ? 'Nalog za otpremanje robe'
                                    : order?.orderStatus === OrderStatus.Transport ||
                                        order?.orderStatus === OrderStatus.Delivered
                                      ? 'Nalog za prijem robe'
                                      : '/'}
                            </span>
                        </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 0, sm: 6 }} />
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body1" component="h6" style={{ fontSize: 18 }}>
                            Izvor: <span style={{ fontWeight: 'bold' }}>{originInfo ?? '/'}</span>
                        </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body1" component="h6" style={{ fontSize: 18 }}>
                            Odredište: <span style={{ fontWeight: 'bold' }}>{destinationInfo ?? '/'}</span>
                        </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 0, sm: 12 }} style={{ height: 15, margin: 0, padding: 0 }} />
                    <Grid2 size={{ xs: 12 }}>
                        <Typography
                            variant="body1"
                            component="h6"
                            style={{ fontSize: 20, borderBottom: '1px solid rgba(155,155,155,1)' }}
                        >
                            Roba
                        </Typography>
                    </Grid2>
                    {order?.orderGoodsItems?.map((ogi) => {
                        return (
                            <Grid2 key={ogi.id} size={{ xs: 12 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        borderBottom: '1px solid rgba(155,155,155,0.3)',
                                    }}
                                >
                                    <Typography>{ogi.article?.title ?? ''}</Typography>
                                    <Typography>{ogi.quantity}</Typography>
                                </Box>
                            </Grid2>
                        );
                    })}
                    <Grid2 size={{ xs: 0, sm: 12 }} style={{ height: 15, margin: 0, padding: 0 }} />
                    {order?.receipts?.map((receipt) => {
                        return (
                            <Grid2 key={receipt.id} size={{ xs: 12 }}>
                                {ReceiptDetails(receipt)}
                            </Grid2>
                        );
                    })}
                    <Grid2 key={order?.waybill?.id} size={{ xs: 12 }}>
                        {WaybillDetails(order?.waybill)}
                    </Grid2>
                </Grid2>
            </DialogContent>
        </ResponsiveDialog>
    );
}
